import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "queen_cart";
const COUPON_KEY = "queen_coupon";

function itemKey(productId, shade) {
  return `${productId}|${shade || ""}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(STORAGE_KEY)) ||
        JSON.parse(localStorage.getItem("fyn_cart")) ||
        []
      );
    } catch {
      return [];
    }
  });
  const [coupon, setCoupon] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(COUPON_KEY)) ||
        JSON.parse(localStorage.getItem("fyn_coupon")) ||
        null
      );
    } catch {
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [bump, setBump] = useState(0);
  const [wilayaFee, setWilayaFee] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
  }, [coupon]);

  const addToCart = useCallback((product, shade = null, quantity = 1) => {
    setItems((prev) => {
      const key = itemKey(product.id, shade);
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: Math.min(i.quantity + quantity, i.stock || 99) } : i
        );
      }
      return [
        ...prev,
        {
          key,
          productId: product.id,
          name: product.name,
          slug: product.slug,
          image: product.images?.[0] || "",
          price: product.price,
          shade,
          quantity,
          stock: product.stock,
        },
      ];
    });
    setBump((b) => b + 1);
  }, []);

  const removeFromCart = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, Math.min(quantity, i.stock || 99)) } : i))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCoupon(null);
    setWilayaFee(null);
  }, []);

  const applyCoupon = useCallback((couponData) => setCoupon(couponData), []);
  const removeCoupon = useCallback(() => setCoupon(null), []);
  const updateWilayaFee = useCallback((fee) => setWilayaFee(fee), []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const deliveryFee = subtotal === 0 ? 0 : wilayaFee;

  let discount = 0;
  if (coupon && subtotal >= (coupon.minOrder || 0)) {
    discount =
      coupon.discountType === "percentage"
        ? Math.round((subtotal * coupon.discountValue) / 100)
        : Math.min(coupon.discountValue, subtotal);
  }
  const total = deliveryFee === null ? null : Math.max(0, subtotal + deliveryFee - discount);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        coupon,
        applyCoupon,
        removeCoupon,
        updateWilayaFee,
        isOpen,
        openCart,
        closeCart,
        itemCount,
        subtotal,
        deliveryFee,
        discount,
        total,
        bump,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
